import { WhatsappStatus } from "@/types/whatsapp";
import { Head } from "@inertiajs/react";
import { useQueryState } from "nuqs";
import { ProfileSection } from "@/components/settings/profile-section";
import { OrganizationSection } from "@/components/settings/organization-section";
import { WhatsappSection } from "@/components/settings/whatsapp-section";
import { AppearanceSection } from "@/components/settings/appearance-section";
import { SoldMessageDetectionSection } from "@/components/settings/sold-message-detection-section";
import { cn } from "@/lib/utils";
import {
  Building2,
  MessageSquare,
  Palette,
  Search,
  User,
} from "lucide-react";

type ProfileData = {
  name: string;
  email: string;
  avatar: string | null;
};

type OrganizationData = {
  name: string;
  slug: string;
  logo: string | null;
} | null;

type SoldMessageDetectionData = {
  enabled: boolean;
  keywords: string | null;
} | null;

type Props = {
  profile: ProfileData;
  organization: OrganizationData;
  whatsapp: WhatsappStatus;
  can_edit_organization_name: boolean;
  can_edit_organization_logo: boolean;
  sold_message_detection: SoldMessageDetectionData;
  can_edit_sold_message_detection: boolean;
};

const tabs = [
  { id: "profile", label: "Perfil", icon: User },
  { id: "organization", label: "Organização", icon: Building2 },
  { id: "whatsapp", label: "WhatsApp", icon: MessageSquare },
  { id: "sold_message_detection", label: "Detecção de vendas", icon: Search },
  { id: "appearance", label: "Aparência", icon: Palette },
] as const;

export default function Show({
  profile,
  organization,
  whatsapp,
  can_edit_organization_name,
  can_edit_organization_logo,
  sold_message_detection,
  can_edit_sold_message_detection,
}: Props) {
  const [activeTab, setActiveTab] = useQueryState("tab", {
    defaultValue: "profile",
  });

  const visibleTabs = tabs.filter(
    (tab) =>
      (tab.id !== "organization" || organization !== null) &&
      (tab.id !== "sold_message_detection" ||
        (can_edit_sold_message_detection && organization !== null)),
  );

  return (
    <>
      <Head title="Configurações" />
      <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Configurações</h1>
        <p className="text-muted-foreground text-sm">
          Gerencie suas preferências e configurações da conta.
        </p>
      </div>

      <div className="flex flex-col gap-6 md:flex-row">
        {/* Sidebar navigation */}
        <nav className="flex md:w-56 md:shrink-0 md:flex-col">
          <ul className="flex gap-1 overflow-x-auto md:flex-col md:overflow-visible">
            {visibleTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <li key={tab.id}>
                  <button
                    onClick={() => setActiveTab(tab.id)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap",
                      activeTab === tab.id
                        ? "bg-accent text-accent-foreground"
                        : "text-muted-foreground hover:bg-accent/50 hover:text-accent-foreground",
                    )}
                  >
                    <Icon className="size-4 shrink-0" />
                    {tab.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Content area */}
        <div className="flex-1 min-w-0">
          {activeTab === "profile" && <ProfileSection profile={profile} />}
          {activeTab === "organization" && organization && (
            <OrganizationSection
              organization={organization}
              canEditName={can_edit_organization_name}
              canEditLogo={can_edit_organization_logo}
            />
          )}
          {activeTab === "whatsapp" && (
            <WhatsappSection whatsapp={whatsapp} />
          )}
          {activeTab === "sold_message_detection" &&
            sold_message_detection && (
              <SoldMessageDetectionSection
                detection={sold_message_detection}
              />
            )}
          {activeTab === "appearance" && <AppearanceSection />}
        </div>
      </div>
    </div>
    </>
  );
}
